import { NSARouterMakerResult } from "namirasoft-account-react"
import { FilterItem, FilterItemOperator } from "namirasoft-core"
import { CacheRow, DatabaseRow, KubernetesRow, MessagingRow, ServerRow, StreamingRow } from "namirasoft-infra"
import { NamirasoftMap } from "namirasoft-site-map"
import { BytesFormatter, NSBox, NSColumn, NSLoading, NSNoData, PercentFormatter } from "namirasoft-site-react"
import { useEffect, useMemo, useRef, useState } from "react"
import ReactFlow, { Controls, Edge, Node, ReactFlowInstance } from "reactflow"
import "reactflow/dist/style.css"
import { InfraImages } from "../InfraImages"
import { InfraRouterProps } from "../InfraRouterProps"
import { InfraUtils } from "../InfraUtils"
import InfraNode, { InfraNodeProps } from "../components/InfraNode"
import useOnlineList from "../hooks/useOnlineList"
import Styles from "./ProjectDiagram.module.css"
import ProjectDiagramTitle from "./ProjectDiagramTitle"

export interface ProjectDiagramProps extends NSARouterMakerResult, InfraRouterProps
{
    project_id: string;
    environment_id: string;
}

export interface ProjectDiagramState
{
    servers: ServerRow[];
    kubers: KubernetesRow[];
    services_map: ServiceMap;
    loading: boolean;
    fullscreen: boolean;
}

interface ServiceMap
{
    cache: CacheRow[];
    database: DatabaseRow[];
    messaging: MessagingRow[];
    streaming: StreamingRow[];
};

type ServiceType = keyof ServiceMap;
const FIT_VIEW_OPTIONS = { padding: 0.1, maxZoom: 1.5 };

interface GroupedRow<T extends ServiceType>
{
    type: T;
    rows: ServiceMap[T];
};

function ProjectDiagram(props: ProjectDiagramProps)
{
    const ReactFlowWrapperRef = useRef<HTMLDivElement>(null);
    const ReactFlowRef = useRef<ReactFlowInstance<any, any>>();

    const [state, setState] = useState<ProjectDiagramState>({
        servers: [],
        kubers: [],
        services_map: { cache: [], database: [], messaging: [], streaming: [], },
        loading: true,
        fullscreen: false,
    });

    const namirasoft = useMemo(
        () => new NamirasoftMap(props.account.token_manager, console.error),
        [props.account.token_manager]
    );

    const { online_list } = useOnlineList(async () =>
    {
        let list: (string | null)[] = [];

        if (state.servers.length > 0)
        {
            let server_list = await props.servers.infra.server.ListOnline([], 1, 100, [], null);
            list.push(...server_list);
        }

        if (state.kubers.length > 0)
        {
            let kubernetes_list = await props.servers.infra.kubernetes.ListOnline([], 1, 100, [], null);
            list.push(...kubernetes_list);
        }

        return list;
    }, [state.servers, state.kubers]);

    useEffect(() =>
    {
        load();
        // eslint-disable-next-line
    }, [props.project_id, props.environment_id]);

    useEffect(() =>
    {
        let resize_observer = new ResizeObserver(() => ReactFlowRef.current?.fitView(FIT_VIEW_OPTIONS));
        if (ReactFlowWrapperRef.current)
            resize_observer.observe(ReactFlowWrapperRef.current);

        return () => resize_observer.disconnect();
        // eslint-disable-next-line
    }, [ReactFlowRef.current]);

    const load = async () =>
    {
        setState(prev => ({ ...prev, loading: true }));

        try
        {
            let servers = await loadServers();
            let kubers = await loadKubers();

            if (servers.count === 0 && kubers.count === 0)
            {
                setState(prev => ({ ...prev, loading: false }));
                return;
            }

            let [cache, database, messaging, streaming] = await Promise.all([
                loadServices("cache"),
                loadServices("database"),
                loadServices("messaging"),
                loadServices("streaming"),
            ]);

            setState((prev) => ({
                ...prev,
                servers: servers.rows,
                kubers: kubers.rows,
                services_map: { cache, database, messaging, streaming },
                loading: false,
            }));
        }
        catch (e)
        {
            props.notifier.onError(e as Error);
            setState(prev => ({ ...prev, loading: false }));
        }
    };

    const loadServers = async () =>
    {
        let filters = [new FilterItem(
            namirasoft.infra.tables.server,
            namirasoft.infra.tables.server.columns.environment_id,
            false,
            FilterItemOperator.all.equals,
            props.environment_id
        )];
        return await props.servers.infra.server.List(filters, 1, 100, [], null);
    }

    const loadKubers = async () =>
    {
        let filters = [new FilterItem(
            namirasoft.infra.tables.kubernetes,
            namirasoft.infra.tables.kubernetes.columns.environment_id,
            false,
            FilterItemOperator.all.equals,
            props.environment_id
        )];
        return await props.servers.infra.kubernetes.List(filters, 1, 100, [], null);
    }

    const loadServices = async <K extends ServiceType>(service: K): Promise<ServiceMap[K]> =>
    {
        let filters = [new FilterItem(
            namirasoft.infra.tables[service],
            namirasoft.infra.tables[service].columns.environment_id,
            false,
            FilterItemOperator.all.equals,
            props.environment_id
        )];
        let response = await props.servers.infra[service].List(filters, 1, 100, [], null);
        return response.rows as ServiceMap[K];
    };

    const toggleFullscreen = () =>
    {
        setState((prev) => ({ ...prev, fullscreen: !prev.fullscreen }));
    }

    const getNodes = (): Node<InfraNodeProps>[] =>
    {
        let { servers, kubers, services_map } = state;

        if (servers.length === 0 && kubers.length === 0)
            return [];

        let nodes: Node<InfraNodeProps>[] = [];

        const COL_WIDTH = 240;
        const ROW_HEIGHT = 160;

        let infrastructure_count = servers.length + kubers.length;
        let infra_per_row = Math.max(1, Math.ceil(Math.sqrt(infrastructure_count) * 1.5));
        let infra_rows = Math.ceil(infrastructure_count / infra_per_row);
        let infrastructure_index = 0;

        const infraPosition = (i: number) => ({
            x: -(infra_per_row * COL_WIDTH) / 2 + (i % infra_per_row) * COL_WIDTH,
            y: Math.floor(i / infra_per_row) * ROW_HEIGHT,
        });

        servers.forEach((server) =>
        {
            nodes.push({
                id: server.id,
                type: "infra_node",
                position: infraPosition(infrastructure_index),
                data: {
                    type: "Infrastructure",
                    label: `${server.name} ${server.meta_profile_os ? `( ${server.meta_profile_os} )` : ""}`.trim(),
                    status: InfraUtils.getNodeStatus(server, online_list),
                    link: namirasoft.infra.tables.server.front_end.getViewLink(server.id),
                    image: InfraImages.server,
                    meta: [
                        { title: "IP Public", value: server.meta_profile_ip_public },
                        { title: "IP Private", value: server.meta_profile_ip_private },
                        { title: "Architecture", value: server.meta_profile_architecture },
                        { title: "OS", value: server.meta_profile_os },
                        { title: "CPU", value: server.meta_profile_cpu },
                        { title: "GPU", value: server.meta_profile_gpu },
                        { title: "Memory", value: new BytesFormatter().format(+(server.meta_profile_memory ?? 0)) },
                        { title: "Disk", value: new BytesFormatter().format(+(server.meta_profile_disk ?? 0)) },
                        { title: "CPU Usage", value: new PercentFormatter().format(server.meta_metric_cpu_usage) },
                        { title: "Memory Usage", value: new PercentFormatter().format(server.meta_metric_memory_usage) },
                        { title: "Disk Usage", value: new PercentFormatter().format(server.meta_metric_disk_usage) },
                    ]
                },
            });
            infrastructure_index++;
        });

        kubers.forEach((kubernetes) =>
        {
            nodes.push({
                id: kubernetes.id,
                type: "infra_node",
                position: infraPosition(infrastructure_index),
                data: {
                    type: "Infrastructure",
                    label: kubernetes.name,
                    status: InfraUtils.getNodeStatus(kubernetes, online_list),
                    link: namirasoft.infra.tables.kubernetes.front_end.getViewLink(kubernetes.id),
                    image: InfraImages.kubernetes,
                    meta: [
                        { title: "Cluster ID", value: kubernetes.meta_profile_cluster_id },
                        { title: "Cluster Name", value: kubernetes.meta_profile_cluster_name },
                        { title: "Cluster Version", value: kubernetes.meta_profile_cluster_version },
                        { title: "Node Count", value: kubernetes.meta_profile_node_count },
                        { title: "Node Count Ready", value: kubernetes.meta_profile_node_count_ready },
                        { title: "Node Count Not Ready", value: kubernetes.meta_profile_node_count_not_ready },
                        { title: "Node Count Master", value: kubernetes.meta_profile_node_count_master },
                        { title: "Node Count Worker", value: kubernetes.meta_profile_node_count_worker },
                        { title: "Total CPU", value: kubernetes.meta_profile_total_cpu },
                        { title: "Total GPU", value: kubernetes.meta_profile_total_gpu },
                        { title: "Total Memory", value: new BytesFormatter().format(parseInt(kubernetes.meta_profile_total_memory ?? "")) },
                        { title: "Total Disk", value: new BytesFormatter().format(parseInt(kubernetes.meta_profile_total_disk ?? "")) },
                        { title: "Total Pod Capacity", value: kubernetes.meta_profile_total_pod_capacity },
                        { title: "Usage CPU", value: (kubernetes.meta_profile_usage_cpu || 0) + "%" },
                        { title: "Usage GPU", value: (kubernetes.meta_profile_usage_gpu || 0) + "%" },
                        { title: "Usage Memory", value: new BytesFormatter().format(parseInt(kubernetes.meta_profile_usage_memory ?? "")) },
                        { title: "Usage Disk", value: new BytesFormatter().format(parseInt(kubernetes.meta_profile_usage_disk ?? "")) },
                        { title: "Requested CPU", value: kubernetes.meta_profile_requested_cpu },
                        { title: "Requested GPU", value: kubernetes.meta_profile_requested_gpu },
                        { title: "Requested Memory", value: new BytesFormatter().format(parseInt(kubernetes.meta_profile_requested_memory ?? "")) },
                        { title: "Requested Disk", value: new BytesFormatter().format(parseInt(kubernetes.meta_profile_requested_disk ?? "")) },
                        { title: "Limit CPU", value: kubernetes.meta_profile_limit_cpu },
                        { title: "Limit Memory", value: new BytesFormatter().format(parseInt(kubernetes.meta_profile_limit_memory ?? "")) },
                        { title: "Limit GPU", value: kubernetes.meta_profile_limit_gpu },
                        { title: "Request Ratio CPU", value: kubernetes.meta_profile_request_ratio_cpu },
                        { title: "Request Ratio GPU", value: kubernetes.meta_profile_request_ratio_gpu },
                        { title: "Request Ratio Memory", value: new BytesFormatter().format(parseInt(kubernetes.meta_profile_request_ratio_memory ?? "")) },
                        { title: "Request Ratio Disk", value: new BytesFormatter().format(parseInt(kubernetes.meta_profile_request_ratio_disk ?? "")) },
                        { title: "Usage Ratio CPU", value: kubernetes.meta_profile_usage_ratio_cpu },
                        { title: "Usage Ratio GPU", value: kubernetes.meta_profile_usage_ratio_gpu },
                        { title: "Usage Ratio Memory", value: new BytesFormatter().format(parseInt(kubernetes.meta_profile_usage_ratio_memory ?? "")) },
                        { title: "Count Namespace", value: kubernetes.meta_profile_count_namespace },
                        { title: "Count Storage Classes", value: kubernetes.meta_profile_count_storage_classes },
                        { title: "Count PV", value: kubernetes.meta_profile_count_pv },
                        { title: "Count PVC", value: kubernetes.meta_profile_count_pvc },
                        { title: "Count Pod", value: kubernetes.meta_profile_count_pod },
                        { title: "Count Pod Running", value: kubernetes.meta_profile_count_pod_running },
                        { title: "Count Deployment", value: kubernetes.meta_profile_count_deployment },
                        { title: "Count Statefulset", value: kubernetes.meta_profile_count_statefulset },
                        { title: "Count Daemonset", value: kubernetes.meta_profile_count_daemonset },
                        { title: "Count Job", value: kubernetes.meta_profile_count_job },
                        { title: "Count Cron Job", value: kubernetes.meta_profile_count_cronjob },
                        { title: "Count Service", value: kubernetes.meta_profile_count_service },
                        { title: "Count Ingress", value: kubernetes.meta_profile_count_ingress },
                        { title: "Count Loadbalancer", value: kubernetes.meta_profile_count_loadbalancer },
                        { title: "Count Network Policy", value: kubernetes.meta_profile_count_network_policy },
                        { title: "Count Secret", value: kubernetes.meta_profile_count_secret },
                        { title: "Count Config", value: kubernetes.meta_profile_count_config },
                    ]
                },
            });
            infrastructure_index++;
        });

        let service_entries = Object.entries(services_map) as [ServiceType, GroupedRow<ServiceType>["rows"]][];

        let service_count = service_entries.reduce((sum, [, rows]) => sum + rows.length, 0);
        let service_per_row = Math.max(1, Math.ceil(Math.sqrt(service_count)));
        let service_y_offset = infra_rows * ROW_HEIGHT;
        let service_index = 0;

        service_entries.forEach(([type, rows]) =>
        {
            rows.forEach((row) =>
            {
                nodes.push({
                    id: row.id,
                    type: "infra_node",
                    position: {
                        x: -(service_per_row * COL_WIDTH) / 2 + (service_index % service_per_row) * COL_WIDTH,
                        y: service_y_offset + Math.floor(service_index / service_per_row) * ROW_HEIGHT,
                    },
                    data: {
                        type: "Service",
                        label: row.name,
                        link: namirasoft.infra.tables[type].front_end.getViewLink(row.id),
                        image: InfraImages[type][row.type as keyof typeof InfraImages[typeof type]],
                    },
                });
                service_index++;
            });
        });

        return nodes;
    };

    const getEdges = (): Edge[] =>
    {
        let services = Object.values(state.services_map).flat();

        return services.map<Edge | null>((service) =>
        {
            let source = service.connect_server_id ?? service.connect_kubernetes_id;
            if (!source)
                return null;
            return {
                id: `e_${service.id}`,
                source,
                target: service.id,
                type: "straight",
                animated: true,
                style: { stroke: "green", strokeWidth: 2 },
            };
        }).filter((edge): edge is Edge => edge !== null);
    }

    let nodes = getNodes();
    let edges = getEdges();

    return (
        <>
            <ProjectDiagramTitle servers={props.servers} project_id={props.project_id} environment_id={props.environment_id} />
            <div
                ref={ReactFlowWrapperRef}
                className={`${Styles.ns_diagram} ${state.fullscreen ? Styles.ns_diagram_fullscreen : ""}`}
            >
                {
                    state.fullscreen &&
                    <ProjectDiagramTitle servers={props.servers} project_id={props.project_id} environment_id={props.environment_id} />
                }
                {
                    state.loading &&
                    <NSLoading />
                }
                {
                    !state.loading && state.servers.length === 0 && state.kubers.length === 0 &&
                    <NSColumn classList={["align-items-center", "justify-content-center", "h-100"]}>
                        <NSNoData lable="There is no data" classList={["pb-4 rounded-4"]} style={{ maxWidth: NSBox.width.double }} />
                    </NSColumn>
                }
                {
                    (state.servers.length > 0 || state.kubers.length > 0) &&
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        nodeTypes={{ infra_node: InfraNode }}
                        minZoom={1}
                        fitView
                        zoomOnScroll={false}
                        onWheelCapture={(e) => e.stopPropagation()}
                        fitViewOptions={FIT_VIEW_OPTIONS}
                        onInit={(instance) => ReactFlowRef.current = instance}
                    >
                        <Controls
                            showInteractive={false}
                            fitViewOptions={FIT_VIEW_OPTIONS}
                            position="bottom-right"
                            className={Styles.ns_rf_controls}
                        >
                            <button
                                className="react-flow__controls-button"
                                onClick={toggleFullscreen}
                                title={state.fullscreen ? "minimize" : "maximize"}
                                aria-label="fit view"
                            >
                                <img
                                    src={`https://static.namirasoft.com/image/concept/${state.fullscreen ? "minimize" : "maximize"}/blue.svg`}
                                    alt={state.fullscreen ? "minimize" : "maximize"}
                                    width={16}
                                    height={16}
                                />
                            </button>
                        </Controls>
                    </ReactFlow>
                }
            </div>
        </>
    )
}

export default ProjectDiagram;